import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import DailyScheduleService from "./DailyScheduleService";
import {
  minutesToTimeInput,
  timeInputToMinutes,
  normalizeActivity,
} from "./data";
import { DS_V2_REFRESH } from "./events";
import ModalPortal from "../common/ModalPortal";
import TimePicker from "./TimePicker";

/**
 * Dual-purpose modal (Add / Edit) for a single Daily-Schedule entry.
 * Two fields — Time + Activity — matching the mock's compact dialog. Save
 * only enables when:
 *
 *   • time is valid (native <input type="time">)
 *   • activity is non-empty and <= 120 chars
 *   • duplicate-time / duplicate-activity checks pass against existingList
 *     (self excluded on edit)
 *   • in Edit mode, at least one field changed vs. the initial values
 *
 * Server does the same duplicate check — this is a UX-only guard so the
 * user sees the error inline before the round-trip.
 *
 * ── Presentation ──────────────────────────────────────────────────────────
 * Px-matched to TWO Figma boards which are the same dialog in two modes:
 *   3495:2854 "Popup · Edit entry"  → title "Edit entry · <activity>",
 *                                     primary "Save entry"
 *   3978:3050 "Popup · Add entry"   → title "Add entry", primary "Add entry"
 * Geometry is identical on both: 400×272, padding 20, column gap 16,
 * radius 16, 1px #E5E7EB border; children Header 28 / Field·Time 58 /
 * Field·Activity 58 / Actions 40. One component, two modes — never fork it.
 *
 * Every class here is namespaced `ds4-*`. The dialog previously borrowed
 * cc-modal-*, sec-field/sec-label/sec-input, sec-btn* and fam-warn, all of
 * which are shared with Chief complaints, Diagnosis, Allergies, Addictions,
 * Medicines, Physical activity and more — restyling them to this board would
 * have deformed those sections. Nothing outside dailySchedule/ uses `ds4-`.
 */

// Activity is free text people write sentences into ("Walk 30 min after
// dinner, avoid stairs"), not a label — so the cap is generous and the row
// wraps rather than truncating. Mirrors constants.ScheduleActivityMaxLen.
const ACTIVITY_MAX_LEN = 500;

/**
 * Close glyph — the real vector recovered from the board
 * (icon-close-3495.svg): two 1.5px strokes with round caps drawn corner to
 * corner of a 14×14 box inset 7px inside the 28×28 button. The button's own
 * white fill / 1px #E5E7EB / 6px radius come from CSS, so only the path is
 * inlined here.
 */
const CloseIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <path
      d="M7 7L21 21M21 7L7 21"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const EntryModal = ({
  patientId,
  date,
  entry,
  existingList,
  version,
  stagingMode,
  onClose,
  onSaved,
  onLocalCommit,
  onConflict,
}) => {
  const isEdit = !!entry;

  const initialTime = isEdit ? minutesToTimeInput(entry.timeMinutes) : "";
  const initialActivity = (entry && entry.activity) || "";

  const [time, setTime] = useState(initialTime);
  const [activity, setActivity] = useState(initialActivity);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !submitting && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const timeMinutes = timeInputToMinutes(time);
  const activityTrimmed = normalizeActivity(activity);
  const activityKey = activityTrimmed.toLowerCase();

  // Duplicate guard — server enforces the same rule; the FE check just
  // saves a round-trip.
  const dupError = useMemo(() => {
    if (timeMinutes == null || !activityTrimmed) return "";
    const selfId = entry && entry.entryId;
    for (const e of existingList || []) {
      if (selfId && e.entryId === selfId) continue;
      if (e.deleted) continue;
      if (e.timeMinutes === timeMinutes) {
        return "Another entry already exists at this time.";
      }
      if (normalizeActivity(e.activity).toLowerCase() === activityKey) {
        return `"${activityTrimmed}" is already on the schedule.`;
      }
    }
    return "";
  }, [timeMinutes, activityKey, activityTrimmed, existingList, entry]);

  const tooLong = activityTrimmed.length > ACTIVITY_MAX_LEN;

  // Dirty check — Edit is a no-op unless the user changed something.
  const isDirty = useMemo(() => {
    if (!isEdit) return true;
    if (time !== initialTime) return true;
    if (activityTrimmed !== normalizeActivity(initialActivity)) return true;
    return false;
  }, [isEdit, time, initialTime, activityTrimmed, initialActivity]);

  // A duplicate does NOT disable the button: the user clicks Save and gets
  // told why it can't go through (a greyed button reads as "not ready", not
  // as "this time is taken").
  const canSubmit =
    !submitting &&
    timeMinutes != null &&
    !!activityTrimmed &&
    !tooLong &&
    isDirty;

  const submit = () => {
    if (!canSubmit) return;
    if (dupError) {
      setFeedback({ kind: "err", text: dupError });
      return;
    }

    // Staging mode — nothing hits the server yet. Hand the trimmed pair up
    // to the parent which merges it into its local `entries` list; the
    // parent's Save button eventually persists the whole batch.
    if (stagingMode) {
      onLocalCommit({
        entryId: entry ? entry.entryId : null,
        timeMinutes,
        activity: activityTrimmed,
      });
      if (onSaved) onSaved();
      return;
    }

    setSubmitting(true);
    setFeedback(null);
    const body = {
      timeMinutes,
      activity: activityTrimmed,
      expectedVersion: version || 0,
    };
    const svc = new DailyScheduleService();
    const done = () => {
      setSubmitting(false);
      EventService.emit(DS_V2_REFRESH);
      if (onSaved) onSaved();
    };
    const failed = (err) => {
      setSubmitting(false);
      // 409 — the parent rebases on `current`; close so the user re-opens
      // against the fresh day rather than re-submitting a stale version.
      if (onConflict && onConflict(err)) {
        onClose();
        return;
      }
      setFeedback({
        kind: "err",
        text: (err && err.message) || "Save failed — please retry",
      });
    };
    if (isEdit) {
      svc.updateEntry(patientId, date, entry.entryId, body, done, failed);
    } else {
      svc.addEntry(patientId, date, body, done, failed);
    }
  };

  const hasAlert = !!dupError || tooLong || !!feedback;

  return (
    <ModalPortal>
      <div className="ds4-backdrop" onMouseDown={submitting ? null : onClose}>
        <div
          className="ds4-modal"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ds4-title"
        >
          {/* Header — 360×28, title left, 28×28 close flush to the right edge */}
          <header className="ds4-head">
            <h2 className="ds4-title" id="ds4-title">
              {isEdit ? "Edit entry" : "Add entry"}
              {isEdit && entry.activity && (
                <span className="ds4-title-sub"> &middot; {entry.activity}</span>
              )}
            </h2>
            <button
              type="button"
              className="ds4-close"
              onClick={submitting ? null : onClose}
              disabled={submitting}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </header>
  
          {/* Field · Time — label 12 + gap 8 + input 38 = 58 */}
          <div className="ds4-field">
            {/* The asterisk is wrapped WITH the label text: .ds4-label is a
                space-between flex row (so the Activity counter sits hard right),
                and a bare sibling span would be flung to the far edge. */}
            <label className="ds4-label" htmlFor="ds-time">
              <span>
                Time<span className="ds4-req">*</span>
              </span>
            </label>
            <div className="ds4-control">
              {/* Our own picker (TimePicker) rather than <input type="time">:
                  the browser's popover is an unstyled blue system control.
                  Same "HH:MM" state, same ds4 input chrome, own clock. */}
              <TimePicker
                id="ds-time"
                value={time}
                onChange={setTime}
                disabled={submitting}
                inputClassName="ds4-input"
              />
            </div>
          </div>
  
          {/* Field · Activity — same 58px archetype, no trailing icon */}
          <div className="ds4-field">
            <label className="ds4-label" htmlFor="ds-activity">
              <span>
                Activity<span className="ds4-req">*</span>
              </span>
              <span className="ds4-count">
                {activityTrimmed.length} / {ACTIVITY_MAX_LEN}
              </span>
            </label>
            <div className="ds4-control ds4-control-grow">
              {/* A textarea, not an input: at 500 characters a single line
                  shows a fortieth of what was typed. It grows to three rows
                  and scrolls after that; newlines collapse in
                  normalizeActivity, so pressing Enter is harmless. */}
              <textarea
                id="ds-activity"
                className={`ds4-input ds4-textarea${tooLong ? " is-error" : ""}`}
                rows={3}
                value={activity}
                onChange={(e) => setActivity(e.target.value)}
                placeholder="e.g. Evening walk"
                disabled={submitting}
                maxLength={ACTIVITY_MAX_LEN + 20}
                aria-invalid={tooLong}
              />
            </div>
          </div>
  
          {/* Not on either board — the boards draw only the resting state. These
              render nothing unless a guard trips, so the resting dialog is
              exactly 400×272. */}
          {hasAlert && (
            <div className="ds4-alerts">
              {dupError && !(feedback && feedback.text === dupError) && (
                <p className="ds4-alert is-error">{dupError}</p>
              )}
              {tooLong && (
                <p className="ds4-alert is-error">
                  Activity is longer than {ACTIVITY_MAX_LEN} characters.
                </p>
              )}
              {feedback && (
                <p className={`ds4-alert is-${feedback.kind}`}>{feedback.text}</p>
              )}
            </div>
          )}
  
          {/* Actions — 360×40 with a 4px top inset; two 175×36 halves, gap 10 */}
          <div className="ds4-actions">
            <button
              type="button"
              className="ds4-btn ds4-btn-sec"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="ds4-btn ds4-btn-pri"
              onClick={submit}
              disabled={!canSubmit}
            >
              {submitting ? "Saving…" : isEdit ? "Save entry" : "Add entry"}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

EntryModal.propTypes = {
  patientId: PropTypes.number.isRequired,
  date: PropTypes.string.isRequired, // YYYY-MM-DD — the day being edited
  entry: PropTypes.object, // undefined = Add; object = Edit
  existingList: PropTypes.array,
  version: PropTypes.number,
  // When true, the modal skips the server call and hands the trimmed pair
  // to onLocalCommit — used by DailySchedule's staging flow.
  stagingMode: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  onLocalCommit: PropTypes.func,
  // Returns true when it handled a 409 (rebased the parent on `current`).
  onConflict: PropTypes.func,
};

export default EntryModal;
